/**
 * Export Dialog Component
 *
 * Provides a comprehensive export interface with:
 * - Format selection (PDF/Word)
 * - Section checkboxes
 * - Cover letter separate toggle
 * - Complete vs. Individual export modes
 */

import React, { useState, useCallback, useMemo } from 'react';
import { X, FileText, FileDown, CheckSquare, Square, Loader2, File } from 'lucide-react';
import toast from 'react-hot-toast';
import { BeautifulDocxExporter } from '../../services/BeautifulDocxExporter';
import { printProposalToPdf } from '../../lib/print-proposal';
import { buildCompanyIdentity } from '../../lib/export/companyIdentity';
import type { Contract } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface ProposalContent {
  coverLetter: string;
  technicalSections: Array<{
    title: string;
    content: string;
  }>;
  feedback?: string;
  contractAnalysis?: string;
  profileAnalysis?: string;
  message?: string;
  aiFeedback?: any;
  deliverablesData?: any;
}

interface ExportDialogProps {
  proposal: ProposalContent;
  contract: Contract;
  onClose: () => void;
  showCitations?: boolean;
  /** Raw `profiles` row (snake_case) for offeror identity on cover/header/footer. */
  profile?: any;
}

interface SectionInfo {
  id: string;
  label: string;
  icon: string;
  available: boolean;
  isSpecial?: boolean; // For Deliverables/Product which aren't in technicalSections
}

// ============================================================================
// SECTION DEFINITIONS
// ============================================================================

const getSections = (proposal: ProposalContent): SectionInfo[] => {
  const sections: SectionInfo[] = [
    { id: 'coverLetter', label: 'Cover Letter', icon: '📨', available: !!proposal.coverLetter },
  ];

  // Add technical sections
  proposal.technicalSections.forEach((section) => {
    const sectionId = section.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    let icon = '📋';

    // Assign appropriate icons based on section title
    if (section.title.toLowerCase().includes('executive')) icon = '📊';
    else if (section.title.toLowerCase().includes('technical')) icon = '🔧';
    else if (section.title.toLowerCase().includes('management')) icon = '👥';
    else if (section.title.toLowerCase().includes('personnel')) icon = '👤';
    else if (section.title.toLowerCase().includes('past performance')) icon = '⭐';
    else if (section.title.toLowerCase().includes('pricing') || section.title.toLowerCase().includes('cost')) icon = '💰';
    else if (section.title.toLowerCase().includes('compliance') || section.title.toLowerCase().includes('far')) icon = '✅';

    sections.push({
      id: sectionId,
      label: section.title,
      icon,
      available: !!section.content,
    });
  });

  // Add Deliverables section if data exists
  const hasDeliverables = proposal.deliverablesData?.deliverables_with_labor?.length > 0;
  sections.push({
    id: 'deliverables',
    label: 'Deliverables',
    icon: '📦',
    available: hasDeliverables,
    isSpecial: true,
  });

  // Add Product section if data exists
  const hasProduct = !!proposal.deliverablesData?.product_definition;
  sections.push({
    id: 'product',
    label: 'Product/Solution',
    icon: '💡',
    available: hasProduct,
    isSpecial: true,
  });

  return sections;
};

// ============================================================================
// EXPORT DIALOG COMPONENT
// ============================================================================

export function ExportDialog({ proposal, contract, onClose, showCitations = false, profile }: ExportDialogProps) {
  const sections = getSections(proposal);

  // Offeror identity (logo + company details) for cover page / header / footer,
  // pulled from the profile with a fallback to the cover letter's companyInfo.
  const identity = useMemo(() => {
    let parsedCoverLetter: any = null;
    try {
      parsedCoverLetter = typeof proposal.coverLetter === 'string'
        ? JSON.parse(proposal.coverLetter)
        : proposal.coverLetter;
    } catch { /* not JSON; identity falls back to profile only */ }
    return buildCompanyIdentity(profile, parsedCoverLetter);
  }, [profile, proposal.coverLetter]);

  // State
  const [selectedSections, setSelectedSections] = useState<string[]>(
    sections.filter(s => s.available).map(s => s.id)
  );
  const [exportFormat, setExportFormat] = useState<'pdf' | 'word'>('pdf');
  const [separateCoverLetter, setSeparateCoverLetter] = useState(false);
  const [exportType, setExportType] = useState<'complete' | 'sections'>('complete');
  const [exporting, setExporting] = useState(false);
  const [includeCitations, setIncludeCitations] = useState(showCitations);

  // Toggle section selection
  const toggleSection = (id: string) => {
    setSelectedSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  // Select/deselect all
  const toggleAll = () => {
    const availableIds = sections.filter(s => s.available).map(s => s.id);
    if (selectedSections.length === availableIds.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(availableIds);
    }
  };

  // Filter proposal based on selected sections
  const getFilteredProposal = useCallback((): ProposalContent => {
    const filteredSections = proposal.technicalSections.filter(section => {
      const sectionId = section.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      return selectedSections.includes(sectionId);
    });

    return {
      ...proposal,
      coverLetter: selectedSections.includes('coverLetter') && !separateCoverLetter
        ? proposal.coverLetter
        : '',
      technicalSections: filteredSections,
      // Keep deliverablesData if selected
      deliverablesData: (selectedSections.includes('deliverables') || selectedSections.includes('product'))
        ? proposal.deliverablesData
        : undefined,
    };
  }, [proposal, selectedSections, separateCoverLetter]);

  // Handle export
  const handleExport = async () => {
    if (selectedSections.length === 0) {
      toast.error('Please select at least one section to export');
      return;
    }

    setExporting(true);

    try {
      if (exportType === 'complete') {
        if (exportFormat === 'pdf') {
          // PDF Export - use react-to-pdf
          await handlePDFExport();
        } else {
          // Word Export - use enhanced BeautifulDocxExporter
          await handleWordExport();
        }
      } else {
        // Section-by-section export (Word only for now)
        await handleSectionExport();
      }

      // Export cover letter separately if requested
      if (separateCoverLetter && selectedSections.includes('coverLetter')) {
        await BeautifulDocxExporter.exportCoverLetterOnly(
          proposal.coverLetter,
          contract,
          identity
        );
      }

      toast.success('Export completed successfully!');
      onClose();
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // PDF Export handler — uses the browser's native print engine on the SAME
  // ExportableProposalView so the PDF matches the on-screen layout exactly
  // (vector text, real pagination, true colors). The user chooses "Save as PDF".
  const handlePDFExport = async () => {
    toast('Opening the print dialog — choose "Save as PDF" as the destination.', {
      icon: '🖨️',
      duration: 5000,
    });
    await printProposalToPdf({
      proposal: getFilteredProposal(),
      contract,
      selectedSections,
      showCitations: includeCitations,
      separateCoverLetter,
      identity,
    });
  };

  // Word Export handler
  const handleWordExport = async () => {
    const filteredProposal = getFilteredProposal();
    await BeautifulDocxExporter.export(filteredProposal, contract, identity);
  };

  // Section-by-section export handler
  const handleSectionExport = async () => {
    for (const sectionId of selectedSections) {
      if (sectionId === 'coverLetter') {
        await BeautifulDocxExporter.exportCoverLetterOnly(
          proposal.coverLetter,
          contract,
          identity
        );
      } else if (sectionId === 'deliverables') {
        // Export deliverables analysis
        if (proposal.deliverablesData) {
          await BeautifulDocxExporter.exportDeliverablesOnly(
            proposal.deliverablesData,
            contract,
            identity
          );
        }
      } else if (sectionId === 'product') {
        // Export product/solution definition
        if (proposal.deliverablesData?.product_definition) {
          await BeautifulDocxExporter.exportProductOnly(
            proposal.deliverablesData.product_definition,
            contract,
            identity
          );
        }
      } else {
        const section = proposal.technicalSections.find(
          s => s.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') === sectionId
        );
        if (section) {
          await BeautifulDocxExporter.exportSection(
            section.title,
            section.content,
            contract,
            identity
          );
        }
      }
      // Small delay between files to prevent browser blocking
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  const availableSections = sections.filter(s => s.available);
  const allSelected = selectedSections.length === availableSections.length;

  return (
    <>
      {/* Export Dialog Modal */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
        <div className="bg-bw-surface dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-bw-border dark:border-gray-700">
            <h2 className="text-xl font-bold text-bw-ink dark:text-white">Export Proposal</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-bw-muted" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Export Type */}
            <div>
              <label className="text-sm font-medium text-bw-ink dark:text-gray-300 mb-2 block">
                Export Type
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setExportType('complete')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    exportType === 'complete'
                      ? 'border-blue-500 bg-bw-primary-tint dark:bg-blue-900/20'
                      : 'border-bw-border dark:border-gray-700 hover:border-bw-border'
                  }`}
                >
                  <FileText className={`h-6 w-6 mx-auto mb-1 ${exportType === 'complete' ? 'text-bw-primary' : 'text-bw-muted'}`} />
                  <div className="text-sm font-medium text-bw-ink dark:text-white">Complete Document</div>
                  <div className="text-xs text-bw-muted">All sections in one file</div>
                </button>
                <button
                  onClick={() => {
                    setExportType('sections');
                    setExportFormat('word'); // Section export only works with Word
                  }}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    exportType === 'sections'
                      ? 'border-purple-500 bg-bw-primary-tint dark:bg-purple-900/20'
                      : 'border-bw-border dark:border-gray-700 hover:border-bw-border'
                  }`}
                >
                  <FileDown className={`h-6 w-6 mx-auto mb-1 ${exportType === 'sections' ? 'text-bw-primary' : 'text-bw-muted'}`} />
                  <div className="text-sm font-medium text-bw-ink dark:text-white">Individual Sections</div>
                  <div className="text-xs text-bw-muted">Separate Word files</div>
                </button>
              </div>
            </div>

            {/* Format Selection - Only show for complete export */}
            {exportType === 'complete' && (
              <div>
                <label className="text-sm font-medium text-bw-ink dark:text-gray-300 mb-2 block">
                  Format
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setExportFormat('pdf')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      exportFormat === 'pdf'
                        ? 'border-red-500 bg-urgent-bg dark:bg-red-900/20'
                        : 'border-bw-border dark:border-gray-700 hover:border-bw-border'
                    }`}
                  >
                    <div className={`text-sm font-medium ${exportFormat === 'pdf' ? 'text-urgent' : 'text-bw-ink dark:text-gray-300'}`}>
                      <File className="h-4 w-4 inline mr-1 text-urgent" />
                      PDF
                    </div>
                    <div className="text-xs text-bw-muted">Exactly matches the app · Save as PDF</div>
                  </button>
                  <button
                    onClick={() => setExportFormat('word')}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      exportFormat === 'word'
                        ? 'border-blue-500 bg-bw-primary-tint dark:bg-blue-900/20'
                        : 'border-bw-border dark:border-gray-700 hover:border-bw-border'
                    }`}
                  >
                    <div className={`text-sm font-medium ${exportFormat === 'word' ? 'text-bw-primary' : 'text-bw-ink dark:text-gray-300'}`}>
                      <File className="h-4 w-4 inline mr-1 text-bw-primary" />
                      Word
                    </div>
                    <div className="text-xs text-bw-muted">Editable, styled document</div>
                  </button>
                </div>
              </div>
            )}

            {/* Section Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-bw-ink dark:text-gray-300">
                  Sections to Include
                </label>
                <button
                  onClick={toggleAll}
                  className="text-xs text-bw-primary hover:text-bw-primary font-medium"
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto border border-bw-border dark:border-gray-700 rounded-lg p-2">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => section.available && toggleSection(section.id)}
                    disabled={!section.available}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                      section.available
                        ? 'hover:bg-bw-surface-subtle dark:hover:bg-gray-800'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {selectedSections.includes(section.id) ? (
                      <CheckSquare className="h-5 w-5 text-bw-primary flex-shrink-0" />
                    ) : (
                      <Square className="h-5 w-5 text-bw-muted flex-shrink-0" />
                    )}
                    <span className="text-lg">{section.icon}</span>
                    <span className="text-sm text-bw-ink dark:text-gray-300 text-left flex-1">
                      {section.label}
                    </span>
                    {!section.available && (
                      <span className="text-xs text-bw-muted">(empty)</span>
                    )}
                    {section.isSpecial && section.available && (
                      <span className="text-xs text-bw-primary bg-bw-primary-tint px-1.5 py-0.5 rounded">special</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Cover Letter Option */}
            {selectedSections.includes('coverLetter') && (
              <div className="p-3 bg-fit-moderate-bg dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <button
                  onClick={() => setSeparateCoverLetter(!separateCoverLetter)}
                  className="w-full flex items-center gap-3"
                >
                  {separateCoverLetter ? (
                    <CheckSquare className="h-5 w-5 text-fit-moderate flex-shrink-0" />
                  ) : (
                    <Square className="h-5 w-5 text-bw-muted flex-shrink-0" />
                  )}
                  <div className="text-left">
                    <div className="text-sm font-medium text-fit-moderate dark:text-amber-200">
                      Export Cover Letter as Separate File
                    </div>
                    <div className="text-xs text-fit-moderate dark:text-amber-400">
                      Cover letter will be exported as its own Word document
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Citations Option */}
            <div className="p-3 bg-bw-surface-subtle dark:bg-gray-800 rounded-lg">
              <button
                onClick={() => setIncludeCitations(!includeCitations)}
                className="w-full flex items-center gap-3"
              >
                {includeCitations ? (
                  <CheckSquare className="h-5 w-5 text-bw-primary flex-shrink-0" />
                ) : (
                  <Square className="h-5 w-5 text-bw-muted flex-shrink-0" />
                )}
                <div className="text-left">
                  <div className="text-sm font-medium text-bw-ink dark:text-gray-200">
                    Include Source Citations
                  </div>
                  <div className="text-xs text-bw-muted">
                    {includeCitations ? 'Citations visible (internal review)' : 'Clean export (submission ready)'}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-bw-border dark:border-gray-700 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-bw-border dark:border-gray-600 rounded-lg text-bw-ink dark:text-gray-300 hover:bg-bw-surface-subtle dark:hover:bg-gray-800 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || selectedSections.length === 0}
              className="flex-1 px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4" />
                  Export {exportFormat.toUpperCase()}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

    </>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
}

export default ExportDialog;
